import { useCallback } from 'react'
import { useMutation } from '@tanstack/react-query'
import { updateItemStatus, setProcurementState } from '../../shared/api/requestItems.api'
import { releaseManufacturerClaim } from '../../shared/api/procurementRequests.api'
import type { RequestItemStatus } from '../../shared/api/types'
import type { RequestItemProcurementState } from '@crm/shared'
import { toast } from '../../shared/ui/toastStore'
import { confirm } from '../../shared/ui/confirmStore'

/**
 * Мутации смены статуса позиции и per-user закупщицкого state прямо из очереди
 * + снятие claim производителя. Возвращает стабильные ссылки `.mutate` (TanStack
 * мемоизирует) — важно для colDefs: нестабильный колбэк пересоздаёт columns и
 * сбрасывает вид AG Grid (жалоба 04.05). Вынесено из ProcurementQueuePage
 * (файл за лимитом 500 строк).
 */
export function useQueueItemStatus(invalidateQueue: () => void) {
  const statusMutation = useMutation({
    mutationFn: ({ itemId, status }: { itemId: string; status: RequestItemStatus }) =>
      updateItemStatus(itemId, status),
    onSuccess: invalidateQueue,
  })

  const procStateMutation = useMutation({
    mutationFn: ({ itemId, state }: { itemId: string; state: RequestItemProcurementState }) =>
      setProcurementState(itemId, state),
    onSuccess: invalidateQueue,
  })

  // Снятие claim производителя (РОЗ/ADMIN): позиция снова свободна для прямого
  // запроса производителю другим закупщиком.
  const onReleaseManufacturerClaim = useCallback(
    (itemId: string, holderName: string) => {
      void (async () => {
        const ok = await confirm({
          title: 'Снять запрос производителю?',
          message: `Канал «производитель» по позиции держит ${holderName}. После снятия прямой запрос производителю сможет отправить другой закупщик.`,
          confirmText: 'Снять',
          danger: true,
        })
        if (!ok) return
        try {
          const res = await releaseManufacturerClaim(itemId)
          toast.success(`Запрос производителю снят (держал: ${res.previousHolder})`)
          invalidateQueue()
        } catch (e) {
          toast.error((e as Error).message ?? 'Не удалось снять запрос производителю')
        }
      })()
    },
    [invalidateQueue],
  )

  return {
    onChangeStatus: statusMutation.mutate,
    onChangeProcState: procStateMutation.mutate,
    onReleaseManufacturerClaim,
  }
}
